import React, { useEffect, useState } from 'react';
import { apiClient } from '../../api/client';
import './SiteNotification.css';

interface Announcement {
    id: string;
    title: string;
    message: string;
    type: 'info' | 'warning' | 'maintenance';
    is_active: boolean;
}

const TYPE_CONFIG = {
    info: {
        icon: 'i',
        label: 'Объявление',
        dismissLabel: 'Понятно, закрыть',
    },
    warning: {
        icon: '!',
        label: 'Предупреждение',
        dismissLabel: 'Понятно, закрыть',
    },
    maintenance: {
        icon: '!',
        label: 'Технические работы',
        dismissLabel: 'Хорошо, понял(а)',
    },
};

const STORAGE_PREFIX = 'site_notification_dismissed_';

const SiteNotification: React.FC = () => {
    const [announcements, setAnnouncements] = useState<Announcement[]>([]);
    const [visible, setVisible] = useState(false);
    const [exitingIds, setExitingIds] = useState<Set<string>>(new Set());

    useEffect(() => {
        let mounted = true;

        const fetchAnnouncements = async (forceShow = false) => {
            try {
                const data = await apiClient.getActiveAnnouncement();
                if (!mounted) return;

                const anns = data.announcements || [];

                // Filter out dismissed announcements
                const activeAnns = anns.filter((ann: Announcement) => {
                    const key = STORAGE_PREFIX + ann.id;
                    return forceShow || !sessionStorage.getItem(key);
                });

                setAnnouncements(activeAnns);

                if (activeAnns.length > 0) {
                    setTimeout(() => {
                        if (mounted) setVisible(true);
                    }, forceShow ? 100 : 600);
                }
            } catch {
                // silently ignore — not critical
            }
        };

        const handleTrigger = () => fetchAnnouncements(true);
        window.addEventListener('site-notification-trigger', handleTrigger);

        fetchAnnouncements();
        return () => {
            mounted = false;
            window.removeEventListener('site-notification-trigger', handleTrigger);
        };
    }, []);

    const handleDismiss = (id: string) => {
        setExitingIds(prev => new Set(prev).add(id));
        sessionStorage.setItem(STORAGE_PREFIX + id, '1');

        setTimeout(() => {
            setAnnouncements(prev => {
                const updated = prev.filter(a => a.id !== id);
                if (updated.length === 0) setVisible(false);
                return updated;
            });
            setExitingIds(prev => {
                const next = new Set(prev);
                next.delete(id);
                return next;
            });
        }, 380);
    };

    if (!visible || announcements.length === 0) return null;

    return (
        <div className="site-notification-backdrop">
            {announcements.map(ann => {
                const cfg = TYPE_CONFIG[ann.type] || TYPE_CONFIG.info;
                const isExiting = exitingIds.has(ann.id);

                return (
                    <div key={ann.id} className={`site-notification-card type-${ann.type}${isExiting ? ' exiting' : ''}`}>
                        {/* Top accent line */}
                        <div className="sn-accent-bar" />

                        {/* Main body */}
                        <div className="sn-body">
                            <div className="sn-icon-circle" aria-hidden="true">
                                {cfg.icon}
                            </div>

                            <div className="sn-content">
                                <div className="sn-header-row">
                                    <span className="sn-badge">{cfg.label}</span>
                                </div>
                                <p className="sn-title">{ann.title}</p>
                                <p className="sn-message">{ann.message}</p>
                            </div>

                            <button
                                className="sn-close"
                                onClick={() => handleDismiss(ann.id)}
                                aria-label="Закрыть уведомление"
                                title="Закрыть"
                            >
                                <i className="fas fa-times" />
                            </button>
                        </div>

                        {/* Footer dismiss button */}
                        <div className="sn-footer">
                            <button className="sn-dismiss-btn" onClick={() => handleDismiss(ann.id)}>
                                {cfg.dismissLabel}
                            </button>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default SiteNotification;
